import { useMemo } from 'react';

import { useCurrentAccount } from '@/store/use-account-store';
import { OrderType } from '@/types';
import { useFormContext } from 'react-hook-form';
import { OrderFormSchema } from '@/features/trade/order/components';

type Options = {
  leverage: number;
};

export default function useAvailableFunds(
  symbol: string,
  { leverage }: Options,
) {
  const form = useFormContext<OrderFormSchema>();

  const orderType = form.watch('order.orderType');
  const reduceOnly = form.watch('order.reduceOnly');
  const isBuy = form.watch('order.isBuy');

  const { availableFunds, unrealizedPnl, markMargin, isLong } =
    useCurrentAccount((state) => {
      const position = state.positions[symbol];
      return {
        availableFunds: state.availableFunds.bigint,
        unrealizedPnl: state.unrealizedPnl.bigint,
        markMargin: position
          ? (position.value.bigint * position.initMarginRatio) / 10n ** 4n
          : 0n,
        isLong: position?.isLong,
      };
    });

  return useMemo(() => {
    let value = 0n;
    if (reduceOnly) {
      value = markMargin;
    } else {
      value = availableFunds;
      if (isLong !== isBuy && orderType === OrderType.MARKET) {
        value += markMargin;
      }
      if (unrealizedPnl > 0n) {
        value += unrealizedPnl;
      }
    }
    if (value < 0n) {
      value = 0n;
    } else {
      value *= BigInt(leverage);
    }
    return value;
  }, [
    availableFunds,
    unrealizedPnl,
    reduceOnly,
    leverage,
    markMargin,
    isBuy,
    isLong,
    orderType,
  ]);
}
